import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, Grid2, IconButton, Stack, Typography } from "@mui/material";
import { times } from "lodash";
import { JSX } from "react";
import { Link, useSearchParams } from "react-router";

import CalendarDayPlan from "~/modules/fieldworker/components/CalendarDayPlan";

import { getCalendarConfig } from "../../util/calendarUtil";

type Props = {
    year: number;
    month: number;
    baseUrl?: string;
    enableDateClick?: boolean;
    onDateClicked?: (day: Date) => void;
    renderDay: (day: Date, isActive: boolean, index: number) => JSX.Element;
};

const monthFormatter = Intl.DateTimeFormat(undefined, {
    month: "long",
});

const dayFormatter = Intl.DateTimeFormat(undefined, {
    weekday: "narrow",
});

export default function GaiaCalendarView({
    year,
    month,
    baseUrl,
    enableDateClick = false,
    onDateClicked,
    renderDay,
}: Props) {
    const [searchParams] = useSearchParams();

    const { days, nextMonth, prefMonth, firstOfMonth } = getCalendarConfig(year, month);
    return (
        <Box>
            <Stack direction="row" alignItems="center" margin={2}>
                {baseUrl && (
                    <IconButton
                        component={Link}
                        to={{
                            pathname: `${baseUrl}/${prefMonth.getFullYear()}/${prefMonth.getMonth() + 1}`,
                            search: searchParams.toString(),
                        }}
                    >
                        <ChevronLeft />
                    </IconButton>
                )}
                <Typography variant="h4" flex={1} textAlign="center">
                    {monthFormatter.format(firstOfMonth)} {year.toString()}
                </Typography>
                {baseUrl && (
                    <IconButton
                        component={Link}
                        to={{
                            pathname: `${baseUrl}/${nextMonth.getFullYear()}/${nextMonth.getMonth() + 1}`,
                            search: searchParams.toString(),
                        }}
                    >
                        <ChevronRight />
                    </IconButton>
                )}
            </Stack>

            <Grid2
                container
                columns={7}
                sx={{
                    "--Grid-borderWidth": "1px",
                    borderColor: "divider",
                    "& > div": {
                        borderRight: "var(--Grid-borderWidth) solid",
                        borderBottom: "var(--Grid-borderWidth) solid",
                        borderColor: "divider",
                        "&:nth-of-type(7n+1)": {},
                        "&:nth-of-type(7n)": {
                            borderRight: 0,
                        },
                    },
                }}
            >
                {times(7, number => (
                    <Grid2 size={1} key={number}>
                        <Typography variant="h6" flex={1} textAlign="center">
                            {dayFormatter.format(new Date(1970, 0, number + 5))}
                        </Typography>
                    </Grid2>
                ))}
                {days.map((day, index) => {
                    return (
                        <CalendarDayPlan
                            key={day.toISOString()}
                            day={day}
                            firstOfMonth={firstOfMonth}
                            onDateClicked={onDateClicked}
                            renderDay={(...rest) => renderDay(...rest, index)}
                            url={
                                enableDateClick
                                    ? {
                                          pathname: `${baseUrl}/${day.getFullYear()}/${day.getMonth() + 1}/${day.getDate()}`,
                                          search: searchParams.toString(),
                                      }
                                    : undefined
                            }
                        />
                    );
                })}
            </Grid2>
        </Box>
    );
}
